<script setup lang="ts">
import {Dialog, DialogHeader, DialogScrollContent, DialogTitle} from "@/core/components/ui/dialog";
import {Attachment} from "@/attachment/interfaces/attachment";
import {storeToRefs} from "pinia";
import {usePatientStore} from "@/stores/patient.store";
import {computed, onMounted, ref} from "vue";
import {attachmentApi, teamApi} from "@/container";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {AttachmentPrefillerOption} from "@/core/http/resources/attachment/AttachmentResource";
import Customer from "@/customer/interfaces/Customer";
import Beneficiary from "@/customer/interfaces/Beneficiary";
import BaseRadio from "@/core/components/base/BaseRadio.vue";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import {Access} from "@/core/interfaces/Access";
import {Label} from "@/core/components/ui/label";
import {Checkbox} from "@/core/components/ui/checkbox";
import {Input} from "@/core/components/ui/input";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/core/components/ui/tooltip";
import {useRouter} from "vue-router";
import {AlertCircle, ArrowRight, UserPlus} from "@lucide/vue";
import {useSystemStore} from "@/stores/system.store";
import MSSanteButton from "@/mssante/components/MSSanteButton.vue";
import {MSSanteAttachment, MSSantePrefill} from "@/mssante/interfaces/SecureMessaging";
import {useToastStore} from "@/stores/toast.store";
import usePatientMSSanteMessages from "@/mssante/composables/usePatientMSSanteMessages";

const {attachment, consumer, options} = defineProps<{
    attachment: Attachment,
    consumer?: Customer | Beneficiary,
    options?: AttachmentPrefillerOption[]
}>();
const open = defineModel<boolean>("open", {required: true});
const emits = defineEmits(['on-close']);

const router = useRouter();
const toast = useToastStore();
const {environment} = useSystemStore();
const {customer} = storeToRefs(usePatientStore());
const consumerKey = ref<string | undefined>(consumer?.consumer_key || customer.value?.consumer_key);
const teamMembers = ref<Access[]>([]);
const teamMembersWithRpps = ref<Access[]>([]);
const teamMembersWithoutRpps = ref<Access[]>([]);
const selectedMemberId = ref<number | undefined>(environment?.id);
const selectedOptions = ref<{ slug: string, value: string | number }[]>(
    selectedMemberId.value ? [{slug: 'accessId', value: selectedMemberId.value}] : []
);
const prefillerOptions = ref<AttachmentPrefillerOption[]>(options ?? []);
const checkedValues = ref<Record<string, (string | number)[]>>({});
const textValues = ref<Record<string, string>>({});
const loading = ref<boolean>(true);

/**
 * Load the options and the prescribers, every checkbox item being checked by default.
 * The modal closes when they cannot be loaded, rather than offering an incomplete document.
 */
onMounted(async () => {
    try {
        if (consumerKey.value) {
            [prefillerOptions.value, teamMembers.value] = await Promise.all([
                attachmentApi().prefillerOptions(attachment.id, consumerKey.value),
                teamApi().prefillers(),
            ]);
            teamMembersWithRpps.value = teamMembers.value.filter(member => !!member.rpps);
            teamMembersWithoutRpps.value = teamMembers.value.filter(member => !member.rpps);
        }
        checkedValues.value = Object.fromEntries(
            checkboxOptions.value.map(option => [option.slug, (option.items ?? []).map(item => item.value)])
        );
    } catch {
        toast.showError({content: 'Les éléments de préremplissage n\'ont pas pu être chargés. Veuillez réessayer.'});
        emits('on-close');
    } finally {
        loading.value = false;
    }
});

const consumers = computed(() => {
    if (!customer.value) {
        return [];
    }

    return [
        customer.value,
        ...(customer.value?.beneficiaries ?? [])
    ];
});

/**
 * The options where a single item is picked among buttons, also the fallback for an unknown type.
 */
const choiceOptions = computed(() => prefillerOptions.value.filter(
    option => option.type !== 'checkbox' && option.type !== 'text'
));

/**
 * The options where several items are checked, all of them by default.
 */
const checkboxOptions = computed(() => prefillerOptions.value.filter(option => option.type === 'checkbox'));

/**
 * The options shown, the free text ones describing the part the MSSanté message is about being
 * hidden while it is unchecked, as they would neither be printed nor sent.
 */
const visibleOptions = computed(() => prefillerOptions.value.filter(
    option => option.type !== 'text' || fillsMessagedPart.value
));

/**
 * The checked checkbox item the MSSanté message is about, on whose line the free text options are shown.
 */
const messagedItem = computed((): { slug: string, value: string | number } | undefined => checkboxOptions.value
    .flatMap(option => (option.items ?? [])
        .filter(item => item.mssante && isValueChecked(option.slug, item.value))
        .map(item => ({slug: option.slug, value: item.value})))[0]);

/**
 * The free text options shown on the line of the item the MSSanté message is about.
 */
const inlineTextOptions = computed(() => messagedItem.value
    ? visibleOptions.value.filter(option => option.type === 'text')
    : []);

/**
 * The options shown as a block of their own.
 */
const blockOptions = computed(() => visibleOptions.value.filter(option => !inlineTextOptions.value.includes(option)));

const isMessagedItem = (slug: string, value: string | number) => {
    return messagedItem.value?.slug === slug && messagedItem.value.value === value;
};

/**
 * The optional free text options shown and filled, with their trimmed text.
 */
const filledTexts = computed((): { option: AttachmentPrefillerOption, value: string }[] => visibleOptions.value
    .filter(option => option.type === 'text')
    .map(option => ({option, value: textValues.value[option.slug]?.trim() ?? ''}))
    .filter(({value}) => value !== ''));

const necessaryItemsCount = computed(() => {
    let count = choiceOptions.value.length;

    if (teamMembers.value.length > 0) {
        count += 1;
    }

    return count;
});

const isComplete = computed(() => selectedOptions.value.length === necessaryItemsCount.value);

/**
 * The consumer the document is filled for, among the customer and their beneficiaries.
 */
const selectedConsumer = computed((): Customer | Beneficiary | undefined => {
    return consumers.value.find(item => item.consumer_key === consumerKey.value) ?? consumer ?? customer.value ?? undefined;
});

const {patientName, vaccinationMessage} = usePatientMSSanteMessages(selectedConsumer);

/**
 * Only vaccination receipts, where a vaccine is picked, carry the vaccination message.
 */
const isVaccination = computed(() => prefillerOptions.value.some(option => option.slug === 'productId'));

/**
 * Whether a checkbox item the MSSanté message is about stays checked, always the case when
 * the server flags none.
 */
const fillsMessagedPart = computed(() => {
    const flagged = checkboxOptions.value.flatMap(option => (option.items ?? [])
        .filter(item => item.mssante)
        .map(item => isValueChecked(option.slug, item.value)));

    return flagged.length === 0 || flagged.some(Boolean);
});

/**
 * Generate the prefilled document with the selected options.
 */
const generateDocument = async (): Promise<Blob> => {
    const prefillerData = selectedOptions.value.reduce((acc, option) => {
        acc[option.slug] = option.value;
        return acc;
    }, {
        ...checkedValues.value,
        ...Object.fromEntries(filledTexts.value.map(({option, value}) => [option.slug, value])),
    } as Record<string, string | number | (string | number)[]>);

    const response = await attachmentApi().prefill(attachment.id, consumerKey.value as string, prefillerData);

    return new Blob([response], {type: response.type || 'application/pdf'});
};

/**
 * Print the compliance document.
 */
const print = () => {
    if (consumerKey.value) {
        generateDocument()
            .then((file) => {
                const url = window.URL.createObjectURL(file);
                const printWindow = window.open(url);
                printWindow?.print();
            })
            .finally(() => {
                emits('on-close');
            });
    }
};

/**
 * The message informing the patient's doctor of the vaccination, signed by the selected prescriber.
 */
const mssantePrefill = computed((): MSSantePrefill => {
    const vaccine = selectedOptions.value.find(option => option.slug === 'productId')?.value ?? '';
    const signatory = teamMembers.value.find(member => member.id === selectedMemberId.value)?.user?.name
        ?? environment?.user?.name ?? '';

    return vaccinationMessage(
        String(vaccine),
        filledTexts.value.map(({option, value}) => `${option.label} : ${value}`),
        signatory,
    );
});

/**
 * Attach the prefilled document to the MSSanté message.
 */
const loadMssanteAttachments = async (): Promise<MSSanteAttachment[]> => {
    return [{name: `${attachment.title}.pdf`, blob: await generateDocument()}];
};

/**
 * Check if a specific value is selected for a given option slug.
 */
const isValueSelected = (slug: string, value: string | number) => {
    return selectedOptions.value.findIndex(option => option.slug === slug && option.value === value) !== -1;
};

/**
 * Check if a value is checked for a given checkbox option slug.
 */
const isValueChecked = (slug: string, value: string | number) => {
    return checkedValues.value[slug]?.includes(value) ?? false;
};

/**
 * Whether a single value is left checked for a checkbox option, which then cannot be unchecked.
 */
const hasSingleChecked = (slug: string) => checkedValues.value[slug]?.length === 1;

/**
 * Whether a value is the last one left checked for a checkbox option.
 */
const isLastChecked = (slug: string, value: string | number) => isValueChecked(slug, value) && hasSingleChecked(slug);

const hoveredItem = ref<string>();
const focusedItem = ref<string>();

/**
 * Whether to explain why a checkbox item cannot be unchecked, while it is hovered or focused and
 * stays the last one checked, so the explanation follows the checked values rather than the pointer.
 */
const explainsLastChecked = (slug: string, value: string | number) => {
    const key = `${slug}-${value}`;

    return (hoveredItem.value === key || focusedItem.value === key) && isLastChecked(slug, value);
};

/**
 * Check or uncheck a value of a checkbox option, the last checked value staying checked.
 */
const toggleCheckedValue = (slug: string, value: string | number, checked: boolean) => {
    const values = checkedValues.value[slug] ?? [];

    if (checked) {
        checkedValues.value = {...checkedValues.value, [slug]: [...values, value]};
    } else if (!hasSingleChecked(slug)) {
        checkedValues.value = {...checkedValues.value, [slug]: values.filter(item => item !== value)};
    }
};

/**
 * Update the selected options with the given slug and value.
 */
const updateSelectedOptions = (slug: string, value: string | number) => {
    const index = selectedOptions.value.findIndex(option => option.slug === slug);

    if (index === -1) {
        selectedOptions.value.push({slug, value});
    } else {
        const copy = [...selectedOptions.value];
        copy[index] = {slug, value};
        selectedOptions.value = copy;
    }
};
</script>

<template>
  <Dialog v-model:open="open" class="z-[1000]">
    <DialogScrollContent>
      <DialogHeader class="px-6">
        <DialogTitle>Choisissez les éléments qui seront prérempli dans le document</DialogTitle>
      </DialogHeader>
      <BaseFlexSpinner v-if="loading" />
      <div v-else>
        <div
          v-for="(option, index) in blockOptions"
          :key="option.slug"
          :class="['flex flex-col gap-3', index > 0 && 'pt-6']"
        >
          <Label :for="option.type === 'text' ? `prefiller-${option.slug}` : undefined">{{ option.label }}</Label>
          <template v-if="option.type === 'checkbox'">
            <TooltipProvider :delay-duration="0">
              <div class="flex flex-row flex-wrap items-center gap-x-5 gap-y-3">
                <template v-for="item in option.items" :key="item.value">
                  <Tooltip :open="explainsLastChecked(option.slug, item.value)">
                    <TooltipTrigger as-child>
                      <div
                        class="flex items-center gap-2"
                        @pointerenter="hoveredItem = `${option.slug}-${item.value}`"
                        @pointerleave="hoveredItem = undefined"
                        @focusin="focusedItem = `${option.slug}-${item.value}`"
                        @focusout="focusedItem = undefined"
                      >
                        <Checkbox
                          :id="`prefiller-${option.slug}-${item.value}`"
                          :model-value="isValueChecked(option.slug, item.value)"
                          :aria-disabled="isLastChecked(option.slug, item.value)"
                          class="aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
                          @update:model-value="checked => toggleCheckedValue(option.slug, item.value, checked === true)"
                        />
                        <Label :for="`prefiller-${option.slug}-${item.value}`" class="font-normal">
                          {{ item.label }}
                        </Label>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>Au moins un élément doit rester coché.</TooltipContent>
                  </Tooltip>
                  <template v-if="isMessagedItem(option.slug, item.value)">
                    <Input
                      v-for="text in inlineTextOptions"
                      :id="`prefiller-${text.slug}`"
                      :key="text.slug"
                      v-model="textValues[text.slug]"
                      :aria-label="text.label"
                      class="h-8 w-52"
                      maxlength="20"
                      :placeholder="`${text.label} (facultatif)`"
                    />
                  </template>
                </template>
              </div>
            </TooltipProvider>
          </template>
          <template v-else-if="option.type === 'text'">
            <Input
              :id="`prefiller-${option.slug}`"
              v-model="textValues[option.slug]"
              class="max-w-xs"
              maxlength="20"
              placeholder="Facultatif"
            />
          </template>
          <div v-else class="flex flex-row flex-wrap items-center gap-3">
            <BaseButton
              v-for="item in option.items"
              :key="item.value"
              :primary="isValueSelected(option.slug, item.label)"
              @click="() => updateSelectedOptions(option.slug, item.label)"
            >
              {{ item.label }}
            </BaseButton>
          </div>
        </div>
        <div v-if="consumers.length > 1" class="flex flex-col pt-6 gap-3">
          <Label>Sélectionnez le patient concerné</Label>
          <div class="flex flex-row flex-wrap gap-x-5 gap-y-3">
            <BaseRadio
              v-for="c in consumers"
              :key="c.consumer_key"
              v-model="consumerKey"
              :label="c.information.first_name + ' ' + c.information.last_name"
              :value="c.consumer_key"
              @update:model-value="value => consumerKey = value"
            />
          </div>
        </div>
        <div v-if="teamMembers.length > 0" class="flex flex-col gap-3 pt-6">
          <div class="flex flex-row items-center justify-between">
            <Label>Sélectionnez le prescripteur</Label>
            <button
              class="flex items-center gap-1 text-xs text-primary hover:underline"
              @click="router.push({name: 'team'})"
            >
              <UserPlus class="size-3.5" />
              Ajouter un assistant prescripteur
            </button>
          </div>
          <div class="flex flex-row flex-wrap gap-x-5 gap-y-3">
            <BaseRadio
              v-for="member in teamMembersWithRpps"
              :key="member.id"
              v-model="selectedMemberId"
              :label="member.user?.name ?? ''"
              :value="member.id"
              @update:model-value="value => updateSelectedOptions('accessId', value)"
            />
          </div>
          <div v-if="teamMembersWithoutRpps?.length" class="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div class="flex gap-3">
              <AlertCircle class="size-5 text-amber-600 shrink-0 mt-0.5" />
              <div class="flex flex-col gap-3 flex-1">
                <div class="flex flex-col gap-1">
                  <p class="text-sm text-gray-700 font-medium">
                    Membres sans RPPS assigné
                  </p>
                  <p class="text-sm text-gray-600">
                    Les membres suivants n'ont pas de RPPS :
                    <span class="font-semibold text-amber-700">
                      {{ teamMembersWithoutRpps.map(member => member.user?.name ?? '').join(', ') }}
                    </span>
                  </p>
                </div>
                <div class="flex items-center gap-2">
                  <BaseButton
                    class="ml-auto group"
                    @click="router.push({name: 'team'})"
                  >
                    <span>Compléter mon équipe</span>
                    <ArrowRight class="size-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
                  </BaseButton>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="flex justify-end items-center gap-3 mt-6">
          <MSSanteButton
            v-if="consumerKey && isVaccination && fillsMessagedPart"
            :disabled="!isComplete"
            :prefills="[mssantePrefill]"
            :attachments-loader="loadMssanteAttachments"
            attachments-required
            :consumer-key="consumerKey"
            :patient-name="patientName"
            @sent="emits('on-close')"
          />
          <BaseButton primary :disabled="!isComplete" @click="print()">
            Valider
          </BaseButton>
        </div>
      </div>
    </DialogScrollContent>
  </Dialog>
</template>
